import { Bookmark02Icon } from '@hugeicons/core-free-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  useFavoriteSession,
  useGetEvent,
  useGetSessionFavoriteCount,
  useGetUserFavoriteSessions,
  useGetUserSessionFavoriteIds,
  useRemoveFavoriteSession,
} from '@sunrise-ui/api-client';
import {
  Colors,
  FirstFavoriteCelebrationModal,
  IconButton,
  TextReg,
  isAndroid,
  isIos,
  useAuthStore,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import React, { useState } from 'react';
import type { ViewStyle } from 'react-native';
import { StyleSheet, View } from 'react-native';

type Props = {
  sessionId: string;
  eventId?: string;
  style?: ViewStyle;
  isLoading?: boolean;
  profileUserId?: string; // If viewing a profile, use cross-event favorites
};

export function SessionFavoriteButton({
  sessionId,
  eventId,
  style,
  isLoading,
  profileUserId,
}: Props) {
  const styles = useDynamicStyles(createStyles);
  const { isAuthenticated, currentUserId } = useAuthStore();
  const [showCelebrationModal, setShowCelebrationModal] = useState(false);

  const { data: event } = useGetEvent(eventId);
  const { data: favoriteCount, isLoading: isLoadingFavoriteCount } =
    useGetSessionFavoriteCount(sessionId, event?.community_id);

  // Fetch user session favorites
  // If viewing a profile, use the cross-event favorites endpoint
  // Otherwise use the per-event favorites for better performance
  const { data: userSessionFavorites } = useGetUserSessionFavoriteIds({
    eventId,
    communityId: event?.community_id,
    enabled: Boolean(isAuthenticated) && !profileUserId,
  });

  const { data: allUserFavorites } = useGetUserFavoriteSessions({
    userId: currentUserId,
    enabled: Boolean(isAuthenticated) && Boolean(profileUserId),
  });

  // Determine if user has favorited by checking if sessionId exists in user's favorited sessions
  const hasFavorited = profileUserId
    ? allUserFavorites?.some((session) => session.id === sessionId)
    : userSessionFavorites?.some((id) => id === sessionId);

  const { mutate: favoriteSession, isPending: isPendingFavorite } =
    useFavoriteSession(sessionId, eventId);
  const { mutate: removeFavoriteSession, isPending: isPendingRemoveFavorite } =
    useRemoveFavoriteSession(sessionId, eventId);

  const isLoadingState =
    isLoading ||
    isLoadingFavoriteCount ||
    isPendingFavorite ||
    isPendingRemoveFavorite;

  const handleCloseCelebrationModal = () => {
    setShowCelebrationModal(false);
  };

  const handleViewProfile = () => {
    router.navigate('/profile');
  };

  const handleFavorite = async () => {
    if (hasFavorited) {
      removeFavoriteSession();
    } else {
      // Check if this is the user's first favorite ever (across all events)
      const hasSeenFirstFavoriteModal = await AsyncStorage.getItem(
        `hasSeenFirstFavoriteModal_${currentUserId}`,
      );
      const isFirstFavoriteEver = !hasSeenFirstFavoriteModal;

      favoriteSession(undefined, {
        onSuccess: async () => {
          // Show celebration modal if this was the user's first favorite ever
          if (isFirstFavoriteEver) {
            setShowCelebrationModal(true);
            // Mark that they've seen the modal
            await AsyncStorage.setItem(
              `hasSeenFirstFavoriteModal_${currentUserId}`,
              'true',
            );
          }
        },
      });
    }
  };

  return (
    <>
      <View style={[styles.favoriteContainer, style]}>
        <View style={styles.starContainer}>
          <IconButton
            icon={Bookmark02Icon}
            loading={isLoadingState}
            hugeIconProps={{
              fill: hasFavorited ? Colors.orange0 : 'transparent',
            }}
            backgroundColor="transparent"
            iconColor={hasFavorited ? Colors.orange0 : Colors.text}
            onPress={() => {
              void handleFavorite();
            }}
            disabled={!sessionId || isLoadingState}
            style={[isLoadingState && styles.starIconLoading]}
          />
          {isLoadingFavoriteCount ? (
            <View style={styles.favoritesSkeleton} />
          ) : (
            <TextReg style={styles.favoriteCount}>{favoriteCount}</TextReg>
          )}
        </View>
      </View>

      <FirstFavoriteCelebrationModal
        visible={showCelebrationModal}
        onClose={handleCloseCelebrationModal}
        onViewProfile={handleViewProfile}
      />
    </>
  );
}

const createStyles = () =>
  StyleSheet.create({
    favoritesSkeleton: {
      width: 20,
      height: 15,
      backgroundColor: Colors.opacity20,
      borderRadius: 8,
      marginVertical: isAndroid ? 1.45 : isIos ? 2.5 : 3,
    },
    favoriteContainer: {
      alignItems: 'center',
    },
    starContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
    },
    starIconLoading: {
      opacity: 0.6,
    },
    favoriteCount: {
      fontSize: 20,
      textAlign: 'center',
    },
  });
